import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Table, Button, Input, Popconfirm, Card, Modal, message, Pagination } from "antd";
import { SearchOutlined, CheckOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { List_Employement_Type_From_Role, Add_Update_Employement_Type, Get_Employment_Type_Users_Count } from "../../../../services/userAPI";
import DeleteIcon from "../../../../Images/deleteIcon.png";
import AddPlusIcon from "../../../../Images/addplusIconWhite.png";
import "./genderConfigurations";
import Delete from "../../../../Images/deleteIcon.png";

const EmployementTypeConfiguration = ({ roleId: propRoleId, accountId: propAccountId }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newEmploymentTypes, setNewEmploymentTypes] = useState([""]);
    const [editingKey, setEditingKey] = useState(null);
    const [editedName, setEditedName] = useState("");
    const [deleteId, setDeleteId] = useState(null);
    const inputRef = useRef(null);
    const location = useLocation();
    const roleId = propRoleId || location.state?.roleId;
    const accountId = propAccountId || location.state?.accountId;

    const { loading, data, refetch } = useQuery(List_Employement_Type_From_Role, {
        variables: { jobRoleId: roleId },
    });

    const [employmentTypes, setEmploymentTypes] = useState([]);

    useEffect(() => {
        if (data) {
            const reversedList = [...(data.listEmployementTypeFromRole || [])].reverse();
            setEmploymentTypes(reversedList);
        }
    }, [data]);


    useEffect(() => {
        if (isModalVisible && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [isModalVisible]);

    const [addUpdateEmployementType, { loading: addLoading }] = useMutation(Add_Update_Employement_Type);
    const [checkEmploymentTypeCount] = useLazyQuery(Get_Employment_Type_Users_Count, { fetchPolicy: "network-only" });

    const filteredData = employmentTypes.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const paginationText = filteredData.length > 0
        ? `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, filteredData.length)} of ${filteredData.length}`
        : "No Data";

    const handleAddMultipleEmploymentTypes = async () => {
        const filtered = newEmploymentTypes.map(type => type.trim()).filter(Boolean);
        if (filtered.length === 0) {
            message.error("Please enter at least one employment type.");
            return;
        }

        try {
            let hasError = false;
            for (const type of filtered) {
                const response = await addUpdateEmployementType({
                    variables: {
                        employementType: type,
                        jobRoleId: roleId
                    }
                });

                if (response.data.addUpdateEmployementType.success) {
                    setEmploymentTypes(prev => [
                        {
                            id: Date.now() + Math.random(), // temporary id
                            name: type,
                            roleId: roleId
                        },
                        ...prev
                    ]);
                } else {
                    hasError = true;
                    message.error(response.data.addUpdateEmployementType.message || `Failed to add "${type}"`);
                }
            }

            if (!hasError) {
                message.success("Employment Types added successfully!");
                setIsModalVisible(false);
                setNewEmploymentTypes([""]);
            }
            refetch();
        } catch (error) {
            console.error("Error adding employment types:", error);
            message.error("Failed to add employment types.");
        }
    };

    const handleDelete = async (id, name, roleId) => {
        setDeleteId(id);
        try {
            const countResponse = await checkEmploymentTypeCount({
                variables: {
                    accountId: Number(accountId),
                    jobroleId: Number(roleId),
                    employmentTypeId: id,
                },
            });
            const count = countResponse?.data?.getEmploymentTypeUsersCount ?? 0;
            if (count > 0) {
                message.error(`Cannot delete: ${count} user(s) are assigned to this employment type.`);
                setDeleteId(null);
                return;
            }

            const response = await addUpdateEmployementType({
                variables: {
                    employmentTypeId: id,
                    employementType: name,
                    jobRoleId: roleId,
                    status: 0,
                },
            });

            if (response.data.addUpdateEmployementType.success) {
                message.success("Employment Type deleted successfully!");
                refetch();
            } else {
                message.error(response.data.addUpdateEmployementType.message);
            }
        } catch (error) {
            console.error("Error deleting employment type:", error);
            message.error("Failed to delete employment type.");
        } finally {
            setDeleteId(null);
        }
    };

    const handleDeleteEmploymentType = (indexToDelete) => {
        setNewEmploymentTypes((prevTypes) => {
            if (prevTypes.length <= 1) {
                return prevTypes;
            }

            return prevTypes.filter(
                (_, index) => index !== indexToDelete
            );
        });
    };

    const handleSave = (key) => {
        setEditingKey(null);
    };

    const columns = [
        {
            title: "Employment Type",
            dataIndex: "name",
            key: "name",
            render: (text, record) => (
                editingKey === record.id ? (
                    <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onPressEnter={() => handleSave(record.id)}
                    />
                ) : (
                    text
                )
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
                    {editingKey === record.id ? (
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => handleSave(record.id)}
                            style={{ marginRight: 8 }}
                        />
                    ) : (
                        <Popconfirm
                            title="Are you sure you want to delete this?"
                            placement="leftTop"
                            overlayClassName="custom-delete-popconfirm"
                            onConfirm={() =>
                                handleDelete(
                                    record.id,
                                    record.name,
                                    record.roleId
                                )
                            }
                            okText={deleteId === record.id ? " " : "Yes"}
                            cancelText="No"
                            okButtonProps={{
                                loading: deleteId === record.id,
                                style: {
                                    backgroundColor: "#AD0101",
                                    color: "#fff",
                                    width: "80px",
                                    height: "40px",
                                    border: "none",
                                    borderRadius: "10px",
                                    marginRight: "35px"
                                },
                            }}
                            cancelButtonProps={{
                                style: {
                                    backgroundColor: "#fff",
                                    color: "black",
                                    width: "80px",
                                    height: "40px",
                                    border: "1px solid black",
                                    borderRadius: "10px",
                                },
                            }}
                        >
                            <img
                                src={DeleteIcon}
                                alt="Delete"
                                style={{ cursor: "pointer" , width: "18px", height: "18px" }}
                            />
                        </Popconfirm>
                    )}
                </div>
            ),
            align: "right",
        },
    ];

    return (
        <div>
            <Card className="search-card">
                <label style={{
                    fontFamily: "Inter", fontSize: "20px", fontSize: "24px",
                    fontWeight: 500,
                    lineHeight: "100%",
                    letterSpacing: "-1%",
                    marginBottom: "2%",
                    marginTop: "1%",

                }}>Employment Type</label>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', borderBottom: 'none', marginTop: '1%' }}>
                    <div className="search-group" style={{ flex: 1 }}>
                        {/* <label>Search</label> */}
                        <div className="search-input-wrapper">
                            <Input
                                value={searchTerm}
                                className="search-input-masters-configuration"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search employment type"
                                suffix={<SearchOutlined />}
                            />
                        </div>
                    </div>

                    <div>
                        <button className="add-designation-btn" onClick={() => setIsModalVisible(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* <img src={AddPlusIcon} alt="Add" style={{ marginRight: "7px", height: "15px", width: "16px" }} /> */}
                            Add Employment
                        </button>
                    </div>
                </div>
                <br />
                <Table
                    columns={columns}
                    dataSource={paginatedData}
                    pagination={false}
                    loading={loading}
                    showHeader={false}
                    rowKey="id"
                />
                {filteredData.length > pageSize && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredData.length}
                            onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            }}
                            // showSizeChanger
                            // pageSizeOptions={['5', '10', '20', '50']}
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        />
                    </div>
                )}
            </Card>

            <Modal
                title="Add New Employment"
                visible={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setNewEmploymentTypes([""]);
                }}
                footer={null}
            >
                <div>
                    {newEmploymentTypes.map((type, index) => (
                        <div
                            key={index}
                            className="relative w-full mb-[10px]"
                        >
                            <Input
                                ref={index === 0 ? inputRef : null}
                                value={type}
                                autoFocus={true}
                                placeholder={`Employment Type ${index + 1}`}
                                onChange={(e) => {
                                    const updated = [...newEmploymentTypes];
                                    updated[index] = e.target.value;
                                    setNewEmploymentTypes(updated);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleAddMultipleEmploymentTypes();
                                    }
                                }}
                                className="w-full pr-12"
                            />

                            {/* Show delete icon only when more than one input exists */}
                            {newEmploymentTypes.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleDeleteEmploymentType(index)}
                                    className="
                    absolute
                    right-3
                    top-1/2
                    -translate-y-1/2
                    flex
                    items-center
                    justify-center
                    bg-transparent
                    border-none
                    cursor-pointer
                    p-0
                    z-10
                "
                                >
                                    <img
                                        src={DeleteIcon}
                                        alt="Delete"
                                        className="w-[20px] h-[20px] object-contain"
                                    />
                                </button>
                            )}
                        </div>
                    ))}

                    <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        {/* Left corner: Add New */}
                        <button
                            className="genderAddNewButton"
                            onClick={() => {
                                if (newEmploymentTypes.length < 10) {
                                    setNewEmploymentTypes([...newEmploymentTypes, ""]);
                                } else {
                                    message.warning("You cannot add more than 10 employment types.");
                                }
                            }}
                            style={{ marginBottom: 16 }}
                        >
                            Add New
                        </button>

                        {/* Right corner: Cancel and Save */}
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button
                                className="gender-configuration-cancel-button"
                                onClick={() => {
                                    setIsModalVisible(false);
                                    setNewEmploymentTypes([""]);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="masters-configuration-add-button"
                                onClick={handleAddMultipleEmploymentTypes}
                                disabled={addLoading}
                                style={{ opacity: addLoading ? 0.7 : 1, cursor: addLoading ? 'not-allowed' : 'pointer' }}
                            >
                                {addLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}
                            </button>
                        </div>
                    </div>

                </div>
            </Modal>
        </div >
    );
};

export default EmployementTypeConfiguration;
